import { hypothesisStatus, knownIssueStatus, lofteUtenFrist, programStatus, submissionStatus, type HypoteseStatusOppforing, type ProgramStatusOppforing, type StatusOppforing } from '../../../theme/theme.ts';
import type { KnownIssueStatus } from '../api/useKnownIssues.ts';
import type { ProgramStatus } from '../api/usePrograms.ts';
import type { ReasonCode } from '../api/useSettings.ts';
import type { StatusType, SubmissionStatus, SubmissionType } from '../api/useSubmissions.ts';

/**
 * Statusnavn per spor, ikon og farger kommer fra `submissionStatus` i theme/theme.ts
 * (brand-ui-guidelines: «Mappen har begge — ikke skriv navnene i en komponent»). Denne
 * fila er panelets ene oppslag mot den.
 */
export function statusOppforing(status: SubmissionStatus): StatusOppforing {
  const o = submissionStatus[status];
  if (!o) throw new Error(`[innspel] Ukjent status «${status}» — theme.ts og kontrakten er ute av synk.`);
  return o;
}

/** DD-22: «Rettet» i feilsporet, «Levert» i ønskesporet. */
export function statusEtikett(status: SubmissionStatus, type: SubmissionType): string {
  return statusOppforing(status).etikett[type];
}

/** Kjent feil-statusen (M2.4): `aktiv` → «Aktiv», `fikset` → «Rettet» (DD-22), stiplet — fra theme.ts. */
export function kjentFeilOppforing(status: KnownIssueStatus): ProgramStatusOppforing {
  const o = knownIssueStatus[status];
  if (!o) throw new Error(`[innspel] Ukjent kjent feil-status «${status}» — theme.ts og kontrakten er ute av synk.`);
  return o;
}

/** Programstatusen (M3.1): etikett, ikon og form fra theme.ts — aldri skrevet i en komponent. */
export function programOppforing(status: ProgramStatus): ProgramStatusOppforing {
  const o = programStatus[status];
  if (!o) throw new Error(`[innspel] Ukjent programstatus «${status}» — theme.ts og kontrakten er ute av synk.`);
  return o;
}

/**
 * Skjulingen lest for mennesker (DD-30 presisert 2026-09-19, avvik 2). Backend
 * (`KnownIssueVisibility.HiddenByFixedIn`) skjuler kortet for ALT som er nyere enn
 * `fixedIn` — også 3.2.1 — og bare når feilen er rettet. Den gamle ordlyden «Skjules for
 * 3.3+» pekte på neste minor og var usann for patchversjonene mellom; den finnes ikke lenger.
 */
export function skjulesForNyereEnn(fixedIn: string): string {
  return `skjules for nyere enn ${fixedIn}`;
}

/**
 * Versjonsområdet lest for mennesker. `versions` er semver-ranger i kontrakten
 * («>=3.2.0 <3.4.0»); detaljruten leser dem som setning. En ukjent form vises RÅTT — en
 * halvveis tolkning er verre enn den rå strengen (`admin-kjente-feil.jsx`, `lesVersjon`).
 */
export function lesVersjon(rang: string): string {
  const fra = /^>=\s*([\d.]+)$/.exec(rang);
  if (fra) return `${fra[1]} og nyere`;
  const til = /^>=\s*([\d.]+)\s*<=\s*([\d.]+)$/.exec(rang);
  if (til) return `${til[1]} til ${til[2]}`;
  const under = /^<\s*([\d.]+)$/.exec(rang);
  if (under) return `eldre enn ${under[1]}`;
  const mellom = /^>=\s*([\d.]+)\s*<\s*([\d.]+)$/.exec(rang);
  if (mellom) return `${mellom[1]} til under ${mellom[2]}`;
  return rang;
}

/** Kategorinavn (DD-22): maskinens felt vist for mennesker. */
export const KATEGORI: Record<StatusType, string> = {
  reviewing: 'Til vurdering',
  active: 'I arbeid',
  complete: 'Ferdig',
  closed: 'Lukket',
};

export const KATEGORIER: StatusType[] = ['reviewing', 'active', 'complete', 'closed'];

/** Datoen slik innsenderen ser den i «Svar innen 3. desember» (DD-28). */
export function datoTekst(iso: string): string {
  return new Date(iso).toLocaleDateString('nb-NO', { day: 'numeric', month: 'long' });
}

/**
 * Setningen innsenderen ser for statusen (DD-08, DD-56) — ORDRETT den portalen og widgeten
 * viser, så behandleren ser hva som faktisk loves, ikke en omskriving. Vises som
 * «Innsenderen har fått: …» for sakens status og «Innsenderen får: …» under det valgte
 * neste steget.
 *
 * Setningen selv bor i `submissionStatus` i `theme/theme.ts` fra 2026-09-14
 * (UX-gjennomgangen C5): den sto hardkodet her OG i `@innspel/web` OG i
 * `@innspel/react-native`, og de tre spriket. Det som står igjen her er panelets
 * TILLEGG — konsekvensen for behandleren, som innsenderen aldri ser.
 */
export function løfte(status: SubmissionStatus, type: SubmissionType, o: { frist: string | null; fixedIn: string }): string {
  const mal = statusOppforing(status).lofte[type === 'onske' ? 'onske' : 'feil'];
  const setning = mal.includes('{frist}')
    ? o.frist
      ? mal.replace('{frist}', datoTekst(o.frist))
      : lofteUtenFrist
    : mal;
  return `«${setning}»${tillegg(status, type, o)}`;
}

/** Det behandleren trenger å vite i tillegg — aldri en del av setningen innsenderen får. */
function tillegg(status: SubmissionStatus, type: SubmissionType, o: { frist: string | null; fixedIn: string }): string {
  switch (status) {
    // F15 (R1.4): «Fristen kan kortes, aldri forlenges» er en REGEL, og regler står ikke på
    // saken. Den gjelder feltet «Kort fristen», og sies der — som grensen datoen må være før.
    case 'vurderes':
      return '';
    case 'planlagt':
      return ' Ingen dato loves.';
    case 'kjent_feil':
      return ' Teller på den kjente feilen.';
    case 'levert':
      return type === 'feil' && o.fixedIn.trim() ? ` Alle som har spurt varsles om ${o.fixedIn.trim()}.` : ' Alle som har spurt varsles.';
    case 'avvist':
      return ' Begrunnelsen din står under.';
    default:
      return '';
  }
}

/** Én grunn for hele skjermen når rollen er leser (ux-flow-architect «Forklarte låser»): banneret øverst i detaljen bærer den, knappene peker på det. */
export const LESER_GRUNN = 'Du har leserolle og kan ikke endre saker';
export const LESER_ID = 'leser-banner';

/**
 * Tilstandsmaskinen i domain-model.md, speilet i klienten så låsen forklares før
 * serveren svarer 409. Serveren er autoriteten. `kjent_feil` og `duplikat` settes aldri
 * herfra — de går via knownIssueId og «Slå sammen».
 */
const KAN_GÅ_TIL: Record<SubmissionStatus, SubmissionStatus[]> = {
  ny: ['vurderes', 'avvist'],
  vurderes: ['planlagt', 'avvist'],
  planlagt: ['bygges'],
  bygges: ['levert'],
  kjent_feil: [],
  levert: [],
  avvist: [],
  duplikat: [],
};

export function kanGåTil(fra: SubmissionStatus, til: SubmissionStatus): boolean {
  return KAN_GÅ_TIL[fra].includes(til);
}

/** Statusene som kan settes herfra: alle unntatt de som aldri settes direkte. */
export const VALGBARE: SubmissionStatus[] = ['ny', 'vurderes', 'planlagt', 'bygges', 'levert', 'avvist'];

/** Neste steg fra en status (DD-56): bare overgangene tilstandsmaskinen har, i VALGBAREs rekkefølge. */
export function nesteSteg(fra: SubmissionStatus): SubmissionStatus[] {
  return VALGBARE.filter((s) => kanGåTil(fra, s));
}

/** Veien videre etter det første neste steget, uten utgangen Avvist: «Planlagt → Retter nå → Rettet». Tom fra en sluttstatus. */
export function veienVidere(fra: SubmissionStatus): SubmissionStatus[] {
  const ut: SubmissionStatus[] = [];
  let s: SubmissionStatus | undefined = nesteSteg(fra).find((x) => x !== 'avvist');
  while (s) {
    ut.push(s);
    s = nesteSteg(s).find((x) => x !== 'avvist');
  }
  return ut.slice(1);
}

/** Alle statuser i flyten, gruppert på kategori (DD-22) — for «Hele flyten» bak et valg (DD-56). */
export const HELE_FLYTEN: SubmissionStatus[] = ['ny', 'vurderes', 'planlagt', 'bygges', 'kjent_feil', 'levert', 'avvist', 'duplikat'];

/**
 * Hovedlinjen i tilstandsmaskinen. F30 (DD-56 presisert 2026-09-18): et steg lenger fram på
 * den kan velges direkte i «Hele flyten»; serveren tar mellomstegene i samme lagring — én
 * audit-rad per steg, ÉN webhook for siste steg. Utgangene (avvist, duplikat, kjent_feil)
 * er aldri «lenger fram»: de har sine egne regler og tas aldri som mellomsteg.
 */
export const HOVEDLINJEN: SubmissionStatus[] = ['ny', 'vurderes', 'planlagt', 'bygges', 'levert'];

/** Stegene lenger fram enn neste steg på hovedlinjen. Fra `ny`: planlagt, bygges, levert. Tom fra en sluttstatus eller en utgang. */
export function stegLengerFram(fra: SubmissionStatus): SubmissionStatus[] {
  const i = HOVEDLINJEN.indexOf(fra);
  return i < 0 ? [] : HOVEDLINJEN.slice(i + 2);
}

/** Mellomstegene serveren tar mellom `fra` og `til`: fra `ny` til `bygges` → vurderes, planlagt. Tom når `til` er neste steg eller ikke ligger lenger fram. */
export function veienVia(fra: SubmissionStatus, til: SubmissionStatus): SubmissionStatus[] {
  const i = HOVEDLINJEN.indexOf(fra);
  const j = HOVEDLINJEN.indexOf(til);
  return i < 0 || j <= i + 1 ? [] : HOVEDLINJEN.slice(i + 1, j);
}

/** Avvisningskodene (kontraktens ReasonCode) med menneskenavn. Fire koder, ingen «annet» (DD-09). */
export const AVVISNINGSKODER: Array<[ReasonCode, string]> = [
  ['ikke_prioritert', 'Ikke prioritert'],
  ['utenfor_omfang', 'Utenfor produktets omfang'],
  ['kan_ikke_gjenskape', 'Kan ikke gjenskape'],
  ['allerede_mulig', 'Finnes allerede'],
];

export function avvisningsnavn(kode: ReasonCode): string {
  return AVVISNINGSKODER.find(([k]) => k === kode)?.[1] ?? kode;
}

/** Svarets tak i kontrakten (ReplyCreate.text maxLength). Tegntelleren sier fra ved 80 % og 100 %. */
export const SVAR_MAKS = 280;

/** Tokennavnet fra theme.ts (camelCase) → CSS-variabelen fra tokens.css. */
export function tokenVar(navn: string): string {
  return `var(--innspel-color-${navn.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)})`;
}

/**
 * Hypotesens status (H2.3): ikon, farge og form fra `hypothesisStatus` i theme/theme.ts.
 * Nøkkelen er `HypothesisStatusKey` — `konkludert` leses som utfallet, så de tre utfallene får
 * hvert sitt ikon. Navnet er IKKE her: det er leietakerens (`HypothesisStatusWording`), og
 * leses fra rommets config.
 */
export function hypoteseStatusNoekkel(status: string, outcome?: string): string {
  return status === 'konkludert' && outcome ? outcome : status;
}

export function hypoteseOppforing(noekkel: string): HypoteseStatusOppforing {
  const o = hypothesisStatus[noekkel];
  if (!o) throw new Error(`[innspel] Ukjent hypotesestatus «${noekkel}» — theme.ts og kontrakten er ute av synk.`);
  return o;
}
